import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";

import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Link,
  SelectionMode,
  Shimmer,
  Stack,
  Text
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import {
  getApplicationNameFromAppCode,
  PltUser
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useSearchUsers,
  useTenantQuery
} from "@libs/api/gateways/plt/plt-gateway.hooks";

import { UnlinkTenantButton } from "./UnlinkTenantButton";

interface CloudTenantsTableProps {
  bpIdUserId: string;
  // Tenant ids already shown in the Premier (federated) section — excluded here
  // so this table lists only the non-PR (CAM / Omni) tenants.
  federatedTenantIds: string[];
  canUnlink?: boolean;
}

const TenantNameCell: FunctionComponent<{ tenantId?: string }> = ({
  tenantId
}) => {
  const navigate = useNavigate();
  // Guard against a null/empty id so we never fetch /tenants/null (which 400s
  // and poisons the shared tenant cache).
  const { data: tenant, isLoading } = useTenantQuery(tenantId!, {
    enabled: !!tenantId
  });

  if (!tenantId) return <Text>-</Text>;
  if (isLoading) return <Shimmer />;

  return (
    <Link onClick={() => navigate(`/tenants/${tenantId}/plt-details`)}>
      {tenant?.name ?? tenantId}
    </Link>
  );
};

const TenantApplicationCell: FunctionComponent<{ tenantId?: string }> = ({
  tenantId
}) => {
  const { data: tenant } = useTenantQuery(tenantId!, { enabled: !!tenantId });
  return (
    <Text>
      {tenant ? getApplicationNameFromAppCode(tenant.application) : ""}
    </Text>
  );
};

export const CloudTenantsTable: FunctionComponent<CloudTenantsTableProps> = ({
  bpIdUserId,
  federatedTenantIds,
  canUnlink
}) => {
  const navigate = useNavigate();
  const searchQuery = useSearchUsers({ bpIdUserId }, { enabled: !!bpIdUserId });

  const excluded = new Set(federatedTenantIds);

  const columns: IColumn[] = [
    {
      key: "tenant",
      name: "Tenant",
      minWidth: 160,
      maxWidth: 280,
      isMultiline: true,
      onRender: (u: PltUser) => <TenantNameCell tenantId={u.tenantId} />
    },
    {
      key: "application",
      name: "Application",
      minWidth: 120,
      maxWidth: 180,
      onRender: (u: PltUser) => <TenantApplicationCell tenantId={u.tenantId} />
    },
    {
      key: "tenantUser",
      name: "Tenant user",
      minWidth: 160,
      maxWidth: 260,
      isMultiline: true,
      onRender: (u: PltUser) => (
        <Link
          onClick={() => navigate(`/tenants/${u.tenantId}/plt-users/${u.id}`)}
        >
          {u.username ||
            `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
            u.id}
        </Link>
      )
    },
    {
      key: "tenantId",
      name: "Tenant ID",
      minWidth: 120,
      maxWidth: 170,
      onRender: (u: PltUser) => <CopyableGuid short value={u.tenantId} />
    },
    {
      key: "tenantUserId",
      name: "Tenant user ID",
      minWidth: 120,
      maxWidth: 170,
      onRender: (u: PltUser) => <CopyableGuid short value={u.id} />
    }
  ];
  if (canUnlink) {
    columns.push({
      key: "actions",
      name: "",
      minWidth: 90,
      maxWidth: 110,
      onRender: (i: PltUser) =>
        i.tenantId && i.bpIdUserId ? (
          <UnlinkTenantButton
            bpIdUserId={i.bpIdUserId}
            tenantId={i.tenantId}
            tenantUserId={i.id}
            isLastFederatedTenant={false}
          />
        ) : null
    });
  }

  return (
    <QueryStateIndicator
      {...searchQuery}
      NoDataNode={<Text>No other linked tenants found.</Text>}
    >
      {(users: PltUser[]) => {
        const members = users.filter(u => !excluded.has(u.tenantId));
        return members.length ? (
          <Stack styles={{ root: { maxWidth: 1200 } }}>
            <DetailsList
              items={members}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              getKey={(u: PltUser) => `${u.tenantId}:${u.id}`}
            />
          </Stack>
        ) : (
          <Text>No other linked tenants found.</Text>
        );
      }}
    </QueryStateIndicator>
  );
};
